import { useRef, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useActiveResume } from "../hooks/useResume";
import { LoadingState } from "./LoadingState";

/** Wraps a single resume-dependent route (Dashboard, Matches, Apply Queue).
 *  Redirects an authenticated user with no active resume to the onboarding
 *  screen instead of letting them land on an empty dashboard/matches page.
 *
 *  - Uses the SAME shared `useActiveResume()` query (queryKey ["resume",
 *    "active"]) that Matches.tsx already reads for its clarity callout, so
 *    wrapping a page here does not add a second network call for it: this
 *    gate's fetch resolves and caches before its children mount, so the
 *    child's own call reads the cache instead of re-fetching.
 *  - Applied per-route (not as a single always-mounted top-level layout) so
 *    it naturally re-mounts — and re-checks — on every navigation between
 *    gated routes, picking up a resume deletion on "the next protected
 *    navigation" rather than staying stuck on a stale in-memory decision.
 *  - Fails OPEN on any error other than the expected 404 ("no active
 *    resume") — a transient failure of this UX check (network blip, 5xx)
 *    must never hard-block an authenticated user from the app; the wrapped
 *    page already handles its own empty state.
 *  - Renders neither the page nor the onboarding screen while the check is
 *    in flight, so there is no dashboard flash before a redirect.
 *
 *  The loading state is shown ONLY until the first answer. After that a refetch (e.g. the page's own observer
 *  mounting on an errored query, which TanStack puts back to "pending") must not unmount the page: that
 *  unmount → error → remount cycle re-requested GET /resumes/active at network speed — 2 000+ calls in 3 s on any
 *  429 / 5xx (e2e request-storm.spec.ts). */
export function ResumeGate({ children }: { children: ReactNode }) {
  const { isLoading, isError, error } = useActiveResume();
  const answered = useRef(false);
  if (!isLoading) answered.current = true;

  if (isLoading && !answered.current) return <LoadingState />;

  const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
  if (isError && status === 404) {
    return <Navigate to="/resume-onboarding" replace />;
  }

  return <>{children}</>;
}
